"use client";

import { useState, useId } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "@/hooks/useTranslation";
import { useIsMobile } from "@/hooks/useIsMobile";
import TermsAndConditionsContent from "./TermsAndConditionsContent";

interface TermsAndConditionsProps {
	isOpen: boolean;
	onClose: () => void;
	onAccept: () => void;
}

export default function TermsAndConditions({
	isOpen,
	onClose,
	onAccept,
}: TermsAndConditionsProps) {
	const { t } = useTranslation();
	const [isAccepted, setIsAccepted] = useState(false);
	const checkboxId = useId();
	const isMobile = useIsMobile();

	const handleAccept = () => {
		if (isAccepted) {
			onAccept();
			onClose();
		}
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<motion.div
					initial={isMobile ? {} : { opacity: 0 }}
					animate={isMobile ? {} : { opacity: 1 }}
					exit={isMobile ? {} : { opacity: 0 }}
					className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
					onClick={onClose}
				>
					<motion.div
						initial={isMobile ? {} : { scale: 0.9, opacity: 0 }}
						animate={isMobile ? {} : { scale: 1, opacity: 1 }}
						exit={isMobile ? {} : { scale: 0.9, opacity: 0 }}
						className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
						onClick={(e) => e.stopPropagation()}
					>
						{/* Header */}
						<div className="flex items-center justify-between p-6 border-b border-gray-200">
							<h2 className="text-2xl font-bold text-gray-900">
								Nastenka en Mallorca
							</h2>
							<button
								type="button"
								onClick={onClose}
								className={`p-2 rounded-full ${
									isMobile 
										? "" 
										: "hover:bg-gray-100 transition-colors"
								}`}
							>
								<XMarkIcon className="w-6 h-6 text-gray-500" />
							</button>
						</div>

						{/* Content */}
						<div className="p-6 overflow-y-auto max-h-[60vh]">
							<TermsAndConditionsContent />
						</div>

						{/* Footer */}
						<div className="p-6 border-t border-gray-200 bg-gray-50">
							<div className="flex items-center space-x-3 mb-4">
								<input
									type="checkbox"
									id={checkboxId}
									checked={isAccepted}
									onChange={(e) => setIsAccepted(e.target.checked)}
									className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
								/>
								<label
									htmlFor={checkboxId}
									className="text-gray-700 cursor-pointer"
								>
									{t("terms.accept")}
								</label>
							</div>

							<div className="flex justify-end space-x-3">
								<button
									type="button"
									onClick={onClose}
									className={`px-6 py-2 text-gray-600 ${
										isMobile 
											? "" 
											: "hover:text-gray-800 transition-colors"
									}`}
								>
									Cancelar
								</button>
								<button
									type="button"
									onClick={handleAccept}
									disabled={!isAccepted}
									className={`px-6 py-2 rounded-lg font-medium ${
										isAccepted
											? `bg-blue-600 text-white ${
												isMobile 
													? "" 
													: "hover:bg-blue-700 transition-all"
											  }`
											: "bg-gray-300 text-gray-500 cursor-not-allowed"
									}`}
								>
									Aceptar
								</button>
							</div>
						</div>
					</motion.div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
